import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaHardHat, 
  FaCheckCircle, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaUserTie, 
  FaMoneyBillWave,
  FaClipboardList,
  FaTimes
} from 'react-icons/fa';

const ManPower = () => {
  const [activeTab, setActiveTab] = useState('ongoing');
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, []);

  // Enhanced dummy data with images
  const ongoingProjects = [
    {
      id: 1,
      name: 'Qiddiya Entertainment City',
      contractor: 'UCC Holding',
      owner: 'Qiddiya Investment Company',
      location: 'Qiddiya, Riyadh',
      value: 'SR 10.2 Billion',
      status: 'ongoing',
      scope: 'Manpower Supply (500+ workers)',
      description: 'Providing skilled labor for the construction of entertainment facilities in the new Qiddiya city, including specialized technicians and general workers.',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 500,
      duration: '3 years',
      startDate: 'January 2022'
    },
    {
      id: 2,
      name: 'King Abdullah Financial District',
      contractor: 'Samsung CCE Joint Venture',
      owner: 'Public Investment Fund',
      location: 'North Riyadh',
      value: 'SR 3.5 Billion',
      status: 'ongoing',
      scope: 'Construction Workforce',
      description: 'Supplying construction workers for the financial district development, including masons, carpenters, and steel fixers.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 350,
      duration: '2.5 years',
      startDate: 'March 2021'
    },
    {
      id: 3,
      name: 'NEOM Industrial City',
      contractor: 'Al Jomaih Company',
      owner: 'NEOM Company',
      location: 'Tabuk Province',
      value: 'SR 8.9 Billion',
      status: 'ongoing',
      scope: 'Skilled Labor Supply',
      description: 'Providing specialized technicians for the NEOM industrial zone construction, including welders, electricians, and equipment operators.',
      image: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 700,
      duration: '4 years',
      startDate: 'July 2022'
    },
    {
      id: 4,
      name: 'Red Sea International Airport',
      contractor: 'Baytur Construction',
      owner: 'Red Sea Development Company',
      location: 'Western Saudi Arabia',
      value: 'SR 2.1 Billion',
      status: 'ongoing',
      scope: 'Technical Manpower',
      description: 'Supplying technical staff for airport construction including civil engineers, surveyors, and quality control specialists.',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 250,
      duration: '2 years',
      startDate: 'October 2022'
    },
    {
      id: 5,
      name: 'Diriyah Gate Mega Project',
      contractor: 'Modern Building Leaders',
      owner: 'Diriyah Gate Development Authority',
      location: 'Diriyah, Riyadh',
      value: 'SR 6.7 Billion',
      status: 'ongoing',
      scope: 'Specialized Construction Team',
      description: 'Providing heritage construction specialists for the Diriyah historical district development.',
      image: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 400,
      duration: '3 years',
      startDate: 'May 2021'
    },
    {
      id: 6,
      name: 'King Salman Energy Park',
      contractor: 'AlQahtani Holding',
      owner: 'Saudi Aramco',
      location: 'Eastern Province',
      value: 'SR 4.3 Billion',
      status: 'ongoing',
      scope: 'Engineering Workforce',
      description: 'Supplying engineering teams for the energy park construction including project managers and field engineers.',
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 300,
      duration: '3.5 years',
      startDate: 'September 2021'
    }
  ];

  const completedProjects = [
    {
      id: 7,
      name: 'Granada Mall Riyadh',
      contractor: 'Al Bawani Co. LTD',
      owner: 'Mazen Hamza Qandeel',
      location: 'Riyadh',
      value: 'SR 2.9 Billion',
      status: 'completed',
      scope: 'Full Manpower Solution',
      description: 'Provided complete workforce solution for the Granada Mall construction including all trades and project management.',
      image: 'https://images.unsplash.com/photo-1486401899868-0e435ed85128?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 450,
      duration: '2 years',
      completionDate: 'June 2020'
    },
    {
      id: 8,
      name: 'King Abdullah Financial Tower',
      contractor: 'Sterling & Wilson Middle East',
      owner: 'Public Investment Fund',
      location: 'North Riyadh',
      value: 'SR 1.8 Billion',
      status: 'completed',
      scope: 'Technical Manpower',
      description: 'Supplied specialized technical staff for the financial tower construction including MEP engineers and technicians.',
      image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 200,
      duration: '1.5 years',
      completionDate: 'December 2019'
    },
    {
      id: 9,
      name: 'Riyadh Metro Project',
      contractor: 'Arriyah New Mobility',
      owner: 'Royal Commission for Riyadh City',
      location: 'Riyadh',
      value: 'SR 22.5 Billion',
      status: 'completed',
      scope: 'Construction Workforce',
      description: 'Provided construction teams for multiple stations of the Riyadh Metro project including tunnel specialists.',
      image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 800,
      duration: '4 years',
      completionDate: 'March 2021'
    },
    {
      id: 10,
      name: 'Jeddah Tower Foundations',
      contractor: 'Saudi Binladin Group',
      owner: 'Kingdom Holding Company',
      location: 'Jeddah',
      value: 'SR 4.7 Billion',
      status: 'completed',
      scope: 'Specialized Engineering Team',
      description: 'Supplied specialized engineering teams for the foundation works of Jeddah Tower.',
      image: 'https://images.unsplash.com/photo-1503177119275-0aa32b3a9368?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 150,
      duration: '1 year',
      completionDate: 'August 2018'
    },
    {
      id: 11,
      name: 'King Abdulaziz International Airport',
      contractor: 'Saudi Binladin Group',
      owner: 'General Authority of Civil Aviation',
      location: 'Jeddah',
      value: 'SR 7.2 Billion',
      status: 'completed',
      scope: 'Full Manpower Solution',
      description: 'Provided complete workforce solution for the airport expansion project including all construction trades.',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 600,
      duration: '3 years',
      completionDate: 'November 2019'
    },
    {
      id: 12,
      name: 'Fadhili Gas Plant',
      contractor: 'Doosan Heavy Industries',
      owner: 'Saudi Aramco',
      location: 'Eastern Province',
      value: 'SR 3.6 Billion',
      status: 'completed',
      scope: 'Technical Workforce',
      description: 'Supplied technical workforce for the gas plant construction including welders, pipefitters, and instrumentation technicians.',
      image: 'https://images.unsplash.com/photo-1581093450021-7b37ec5bd6c9?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
      workers: 350,
      duration: '2.5 years',
      completionDate: 'May 2020'
    }
  ];

  const featuredProject = activeTab === 'ongoing' ? ongoingProjects[0] : completedProjects[0];
  const projectList = activeTab === 'ongoing' ? ongoingProjects.slice(1) : completedProjects.slice(1);

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "backOut"
      }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.3
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <motion.div 
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={containerVariants}
      className="font-sans bg-white text-gray-800 pt-16"
    >
      {/* Header Section with Animation */}
      <motion.div 
        variants={itemVariants}
        className="bg-gradient-to-r from-[#7bbf42] to-[#5a9e2a] py-12 px-4 sm:px-6 lg:px-8 shadow-lg"
      >
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Our Manpower <span className="text-[#f9b414]">Solutions</span>
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-4 text-xl text-white/90 max-w-3xl mx-auto"
          >
            Delivering skilled workforce for the most ambitious construction projects in Saudi Arabia
          </motion.p>
        </div>
      </motion.div>

      {/* Toggle Section */}
      <motion.div 
        variants={itemVariants}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex rounded-full bg-gray-100 p-1 shadow-md border-2 border-[#7bbf42]">
            <button
              onClick={() => setActiveTab('ongoing')}
              className={`px-8 py-3 rounded-full font-bold transition-all duration-300 flex items-center ${
                activeTab === 'ongoing'
                  ? 'text-white bg-[#7bbf42] shadow-lg transform hover:scale-105'
                  : 'text-[#040404] hover:bg-gray-200'
              }`}
            >
              <FaHardHat className="mr-2" /> On Going Projects
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-8 py-3 rounded-full font-bold transition-all duration-300 flex items-center ${
                activeTab === 'completed'
                  ? 'text-white bg-[#7bbf42] shadow-lg transform hover:scale-105'
                  : 'text-[#040404] hover:bg-gray-200'
              }`}
            >
              <FaCheckCircle className="mr-2" /> Completed Projects
            </button>
          </div>
        </motion.div>

        {/* Content Section */}
        <div className="space-y-12">
          <motion.div 
            variants={itemVariants}
            className="flex justify-between items-center mb-8"
          >
            <motion.h2 
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-3xl font-bold text-[#040404]"
            >
              {activeTab === 'ongoing' ? (
                <span>Current <span className="text-[#7bbf42]">Projects</span></span>
              ) : (
                <span>Completed <span className="text-[#f9b414]">Projects</span></span>
              )}
            </motion.h2>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center text-[#7bbf42] font-semibold hover:text-[#f9b414] transition-colors"
            >
              View All Projects
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 ml-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </motion.button>
          </motion.div>

          {/* Featured Project */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab === 'ongoing' ? 'ongoing-featured' : 'completed-featured'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden ${
                activeTab === 'ongoing' ? 'border-l-8 border-[#f9b414]' : 'border-l-8 border-[#7bbf42]'
              } transition-all hover:shadow-2xl`}
            >
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/3 h-64 md:h-auto overflow-hidden">
                  <motion.img
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1 }}
                    src={featuredProject.image}
                    alt={featuredProject.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:w-2/3">
                  <motion.h3 
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-3xl font-bold text-[#040404] mb-4"
                  >
                    {featuredProject.name}
                  </motion.h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <motion.div
                      variants={itemVariants}
                      className="flex items-start"
                    >
                      <FaUserTie className="text-[#7bbf42] text-xl mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Project Owner</p>
                        <p className="font-medium">{featuredProject.owner}</p>
                      </div>
                    </motion.div>
                    <motion.div
                      variants={itemVariants}
                      className="flex items-start"
                    >
                      <FaBuilding className="text-[#7bbf42] text-xl mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Contractor</p>
                        <p className="font-medium">{featuredProject.contractor}</p>
                      </div>
                    </motion.div>
                    <motion.div
                      variants={itemVariants}
                      className="flex items-start"
                    >
                      <FaMapMarkerAlt className="text-[#7bbf42] text-xl mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{featuredProject.location}</p>
                      </div>
                    </motion.div>
                    <motion.div
                      variants={itemVariants}
                      className="flex items-start"
                    >
                      <FaMoneyBillWave className="text-[#7bbf42] text-xl mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Project Value</p>
                        <p className="font-medium">{featuredProject.value}</p>
                      </div>
                    </motion.div>
                    <motion.div
                      variants={itemVariants}
                      className="flex items-start"
                    >
                      {featuredProject.status === 'ongoing' ? (
                        <FaHardHat className="text-[#f9b414] text-xl mt-1 mr-3" />
                      ) : (
                        <FaCheckCircle className="text-[#7bbf42] text-xl mt-1 mr-3" />
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className={`font-medium ${
                          featuredProject.status === 'ongoing' ? 'text-[#f9b414]' : 'text-[#7bbf42]'
                        }`}>
                          {featuredProject.status === 'ongoing' ? 'In Progress' : 'Completed'}
                        </p>
                      </div>
                    </motion.div>
                    <motion.div
                      variants={itemVariants}
                      className="flex items-start"
                    >
                      <FaClipboardList className="text-[#7bbf42] text-xl mt-1 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Scope of Work</p>
                        <p className="font-medium">{featuredProject.scope}</p>
                      </div>
                    </motion.div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleViewDetails(featuredProject)}
                    className="mt-6 px-6 py-3 bg-[#7bbf42] hover:bg-[#f9b414] text-white font-semibold rounded-lg transition-colors"
                  >
                    View Full Details
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Project List */}
          <motion.div 
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12"
          >
            <AnimatePresence>
              {projectList.map((project, index) => (
                <motion.div
                  key={`${activeTab}-${index}`}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={cardVariants}
                  custom={index}
                  whileHover={{ y: -10, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                  className={`bg-white rounded-xl shadow-lg overflow-hidden ${
                    activeTab === 'ongoing' ? 'border-t-4 border-[#7bbf42]' : 'border-t-4 border-[#f9b414]'
                  } transition-all`}
                >
                  <div className="h-48 overflow-hidden">
                    <motion.img
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 + 0.5 }}
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="font-bold text-xl mb-2">{project.name}</h4>
                    <p className="text-gray-600 mb-4">[{project.contractor}]</p>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <span
                          className={`inline-block w-3 h-3 rounded-full mr-2 ${
                            project.status === 'ongoing' ? 'bg-[#f9b414]' : 'bg-[#7bbf42]'
                          }`}
                        ></span>
                        <span className="text-sm">
                          {project.status === 'ongoing' ? 'On Going' : 'Completed'}
                        </span>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleViewDetails(project)}
                        className="text-sm text-[#7bbf42] font-semibold hover:text-[#f9b414] transition-colors"
                      >
                        View Details
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.div>

      {/* Project Details Modal */}
      <AnimatePresence>
        {showModal && selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="text-2xl" />
              </button>
              
              <div className="p-8">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="md:w-1/3">
                    <img 
                      src={selectedProject.image} 
                      alt={selectedProject.name} 
                      className="w-full h-auto rounded-lg shadow-md"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="text-3xl font-bold text-[#040404] mb-4">
                      {selectedProject.name}
                    </h3>
                    <p className="text-gray-600 mb-6">{selectedProject.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="flex items-start">
                        <FaUserTie className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Project Owner</p>
                          <p className="font-medium">{selectedProject.owner}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaBuilding className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Contractor</p>
                          <p className="font-medium">{selectedProject.contractor}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Location</p>
                          <p className="font-medium">{selectedProject.location}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaMoneyBillWave className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Project Value</p>
                          <p className="font-medium">{selectedProject.value}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        {selectedProject.status === 'ongoing' ? (
                          <FaHardHat className="text-[#f9b414] text-xl mt-1 mr-3" />
                        ) : (
                          <FaCheckCircle className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        )}
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <p className={`font-medium ${
                            selectedProject.status === 'ongoing' ? 'text-[#f9b414]' : 'text-[#7bbf42]'
                          }`}>
                            {selectedProject.status === 'ongoing' ? 'In Progress' : 'Completed'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaClipboardList className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Scope of Work</p>
                          <p className="font-medium">{selectedProject.scope}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaUserTie className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">Workers Supplied</p>
                          <p className="font-medium">{selectedProject.workers}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <FaClipboardList className="text-[#7bbf42] text-xl mt-1 mr-3" />
                        <div>
                          <p className="text-sm text-gray-500">
                            {selectedProject.status === 'ongoing' ? 'Project Duration' : 'Project Duration'}
                          </p>
                          <p className="font-medium">{selectedProject.duration}</p>
                        </div>
                      </div>
                      {selectedProject.status === 'ongoing' ? (
                        <div className="flex items-start">
                          <FaClipboardList className="text-[#7bbf42] text-xl mt-1 mr-3" />
                          <div>
                            <p className="text-sm text-gray-500">Start Date</p>
                            <p className="font-medium">{selectedProject.startDate}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start">
                          <FaClipboardList className="text-[#7bbf42] text-xl mt-1 mr-3" />
                          <div>
                            <p className="text-sm text-gray-500">Completion Date</p>
                            <p className="font-medium">{selectedProject.completionDate}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowModal(false)}
                        className="px-6 py-3 bg-[#7bbf42] hover:bg-[#f9b414] text-white font-semibold rounded-lg transition-colors"
                      >
                        Close
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ManPower;